/* Search modal behaviour layered on top of the shared <info-drawer> shell.

   The generic info-drawer handles open/close, backdrop, Esc, focus trap and
   body scroll lock, and does nothing special on close. This file adds the
   search-specific close behaviour: clear the query + collapse predictive
   results.

   Focusing the input on open is NOT done here. It has to happen synchronously
   inside the opening click or mobile will not raise the keyboard, and this
   observer already fires too late for that — so the input opts in with
   [data-drawer-autofocus] and info-drawer focuses it directly. AAS-4647

   It also shows the submit (arrow) button only while the input is non-empty,
   mirroring how the reset button is toggled.

   Hooked via a MutationObserver on the panel's aria-hidden attribute, which
   info-drawer toggles on every open/close. */

(() => {
  const drawer = document.getElementById('SearchDrawer');
  if (!drawer) return;

  const getInput = () => drawer.querySelector('input[type="search"]');
  const predictive = () => drawer.querySelector('predictive-search');

  // Slide the submit arrow in only once the user has typed something
  // (.is-visible drives the masked translate animation in search-modal.css).
  const syncSubmitButton = () => {
    const input = getInput();
    const submit = drawer.querySelector('.search__button');
    if (!input || !submit) return;
    submit.classList.toggle('is-visible', input.value.trim().length > 0);
  };

  /* Runs on close AND on open (AAS-5275): the drawer must always start
     empty, but clearing only on close misses the term coming back from
     elsewhere — on /search the Liquid input is server-prefilled with
     search.terms, and a bfcache restore brings back the whole typed state. */
  const resetSearch = () => {
    const input = getInput();
    if (input) input.value = '';

    const search = predictive();
    if (search) {
      /* info-drawer focuses the input synchronously on open — while it still
         holds the server-side search.terms prefill — and predictive-search's
         onFocus fires a suggestions fetch for it. Clearing the input alone
         loses that race: the late response re-opens the results for the term
         we just wiped. Mirror the component's own onFormReset: forget the
         term and abort the in-flight fetch. */
      search.searchTerm = '';
      if (search.abortController) {
        search.abortController.abort();
        search.abortController = new AbortController();
      }
      search.removeAttribute('open');
      search.removeAttribute('loading');
      search.setAttribute('results', 'false');
    }

    const reset = drawer.querySelector('button[type="reset"]');
    if (reset) reset.classList.add('hidden');

    syncSubmitButton();
  };

  const input = getInput();
  if (input) input.addEventListener('input', syncSubmitButton);

  /* AAS-5275: size the mobile panel to the VISUAL viewport, not the layout
     viewport. 100dvh ignores the on-screen keyboard (and lags the collapsing
     URL bar), so the panel — and the "Search for" footer pinned to its bottom
     — extended under the keyboard, and a 90dvh clamp left a dead white band
     when it was closed. While the drawer is open, mirror
     visualViewport.height into --search-drawer-vvh on the drawer; the ≤989px
     CSS uses it with a 100dvh fallback (no visualViewport → old behaviour).
     Listeners are attached only while open so a resizing desktop window or
     rotating phone doesn't pay for it in the background. */
  const vv = window.visualViewport;
  const syncViewportHeight = () => {
    drawer.style.setProperty('--search-drawer-vvh', `${vv.height}px`);
    /* The keyboard only SHRINKS the visual viewport; the layout viewport the
       fixed drawer is anchored to stays full-height behind it, so a swipe can
       PAN the visual viewport and drag the drawer off the top of the screen
       (visual-viewport panning, not document scroll — the body scroll lock
       cannot stop it). Mirror the pan offset and translate the overlay with
       it so the drawer stays glued to what is actually visible. */
    drawer.style.setProperty('--search-drawer-vvt', `${vv.offsetTop}px`);
  };
  const onOpen = () => {
    resetSearch();
    if (!vv) return;
    syncViewportHeight();
    vv.addEventListener('resize', syncViewportHeight);
    vv.addEventListener('scroll', syncViewportHeight);
  };
  const releaseViewport = () => {
    if (!vv) return;
    vv.removeEventListener('resize', syncViewportHeight);
    vv.removeEventListener('scroll', syncViewportHeight);
    drawer.style.removeProperty('--search-drawer-vvh');
    drawer.style.removeProperty('--search-drawer-vvt');
  };

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.attributeName !== 'aria-hidden') continue;
      if (drawer.getAttribute('aria-hidden') === 'true') {
        resetSearch();
        releaseViewport();
      } else {
        onOpen();
      }
    }
  });

  observer.observe(drawer, { attributes: true, attributeFilter: ['aria-hidden'] });
})();
